import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/** Approximate tab bar height (content + padding) excluding safe-area. */
export const MOBILE_TAB_BAR_HEIGHT_PX = 56;

/**
 * chromeMode (persistent chrome policy):
 * - tabs: bottom tab bar visible (default)
 * - immersive: hide tabs — Graph canvas only
 * - focus: deprecated alias of tabs (kept for callers)
 *
 * Never hide tabs for the soft keyboard — Ask autofocus was trapping users
 * with no way to leave the screen.
 */
const MobileChromeContext = createContext(null);

const DEFAULT_CHROME = {
  chromeMode: "tabs",
  contextTitle: "",
  contextSubtitle: "",
  onBack: null,
  backLabel: "",
  headerTrailing: null,
  hideShellHeader: false,
  actionTray: null,
};

function normalizeChromeMode(mode, onBack) {
  let next = String(mode || "tabs").trim() || "tabs";
  if (next === "focus") next = "tabs";
  if (next === "immersive" && typeof onBack !== "function") {
    next = "tabs";
  }
  return next;
}

export function MobileChromeProvider({ children }) {
  const [chrome, setChromeState] = useState(DEFAULT_CHROME);

  const setChrome = useCallback((patch) => {
    setChromeState((prev) => {
      if (!patch || typeof patch !== "object") return prev;
      const next = { ...prev, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, "onBack")) {
        next.onBack = patch.onBack;
      }
      if (Object.prototype.hasOwnProperty.call(patch, "headerTrailing")) {
        next.headerTrailing = patch.headerTrailing;
      }
      if (Object.prototype.hasOwnProperty.call(patch, "actionTray")) {
        next.actionTray = patch.actionTray;
      }
      next.chromeMode = normalizeChromeMode(next.chromeMode, next.onBack);
      return next;
    });
  }, []);

  const resetChrome = useCallback(() => {
    setChromeState(DEFAULT_CHROME);
  }, []);

  // Publish CSS var for sticky footers / CookieNotice
  useEffect(() => {
    const hideTabs = chrome.chromeMode === "immersive";
    const bottom = hideTabs
      ? "0px"
      : `calc(${MOBILE_TAB_BAR_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`;
    document.documentElement.style.setProperty(
      "--mobile-chrome-bottom",
      bottom
    );
    document.documentElement.style.setProperty(
      "--mobile-tab-visible",
      hideTabs ? "0" : "1"
    );
    return undefined;
  }, [chrome.chromeMode]);

  const tabsHidden = chrome.chromeMode === "immersive";

  const value = useMemo(
    () => ({
      ...chrome,
      keyboardOpen: false,
      tabsHidden,
      setChrome,
      resetChrome,
    }),
    [chrome, tabsHidden, setChrome, resetChrome]
  );

  return (
    <MobileChromeContext.Provider value={value}>
      {children}
    </MobileChromeContext.Provider>
  );
}

export function useMobileChrome() {
  const ctx = useContext(MobileChromeContext);
  if (!ctx) {
    return {
      ...DEFAULT_CHROME,
      keyboardOpen: false,
      tabsHidden: false,
      setChrome: () => {},
      resetChrome: () => {},
    };
  }
  return ctx;
}

/** Route → default mobile context title when page does not override. */
export function defaultTitleForRoute(route) {
  const map = {
    home: "Ask",
    "insights-inbox": "Inbox",
    "baseline-approvals": "Approvals",
    "architecture-gallery": "Your map",
    reports: "Reports",
    explorer: "Browse map",
    admin: "Admin",
    help: "Help",
    settings: "Settings",
  };
  return map[route] || "DecisionPen";
}
